import { FilterEnum, IFilter, LinkEnum } from 'components/Filter/SingleFilter'
import { HonestTable, IHonestTableColumn, IHonestTableMenuOption } from 'components/HonestTable/HonestTable'
import { NavBar } from 'components/NavBar/NavBar'
import { useState } from 'react'
import { Button, Flex, MainHeadline, PageFooter, PageWrapper } from 'styling/GlobalStyles'
import { BodyText, OtherText } from 'styling/Typography'
import { formatDateForDisplay, getParams, handlePagination } from 'utils/helpers'
import {
    PlanCode,
    StatusBadge,
    TargetCell,
    ProgressTrack,
    ProgressFill,
    QueueHealthChip,
} from '../Operations.styled'
import { useNavigate } from 'react-router-dom'
import { Cell } from 'components/HonestTable/HonestTable.styled'
import {
    cancelOperationPlan,
    clearOperationPlans,
    completeOperationPlan,
    getOperationPlans,
    pauseOperationPlan,
    resumeOperationPlan,
} from 'features/operations/plansSlice'
import useQueryPagination from 'hooks/useQueryPagination'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { Pagination } from 'components/Pagination/Pagination'
import { NoData } from 'components/NoData'
import { IGetAllOperationPlansSingleResponse } from 'types/api/operationsPlansApi.interface'
import { planStatuses, planPriorities } from './plansData'


export const Plans = () => {
    const [params, setParams] = useState<any>(getParams({ location, limit: 30 }))
    const navigate = useNavigate()
    const dispatch = useDispatch<AppDispatch>()
    const { data, totalPages } = useSelector((state: RootState) => state.operationPlans)


    const columns: IHonestTableColumn<IGetAllOperationPlansSingleResponse>[] = [
        {
            id: 'plan',
            header: 'Plan',
            sortable: true,
            sticky: true,
            minWidth: '14rem',
            cell: (item) => (
                <Cell>
                    <BodyText weight='600'>{item.name}</BodyText>
                    <PlanCode>{item.code}</PlanCode>
                </Cell>
            ),
        },
        {
            id: 'site',
            header: 'Site',
            cell: (item) => <BodyText>{item.site?.name || '-'}</BodyText>,
        },
        {
            id: 'priority',
            header: 'Priority',
            cell: (item) => <BodyText>{planPriorities.find((priority) => priority.value === item.priority)?.label}</BodyText>,
        },
        {
            id: 'status',
            header: 'Status',
            cell: (item) => (
                <StatusBadge hexColor={planStatuses.find((status) => status.value === item.status)?.color}>{planStatuses.find((status) => status.value === item.status)?.label}</StatusBadge>
            ),
        },
        {
            id: 'target',
            header: 'Target',
            cell: (item) => (
                <TargetCell>
                    <OtherText>
                        {item.targetValue} {item.targetUnit?.name}
                    </OtherText>
                    {item.targetValue > 0 && (
                        <ProgressTrack>
                            <ProgressFill style={{ width: `${item.targetValue}%` }} />
                        </ProgressTrack>
                    )}
                </TargetCell>
            ),
        },
        {
            id: 'boardVisibility',
            header: 'Board Visibility',
            align: 'right',
            cell: (item) => <BodyText>{item.boardVisibility === 1 ? 'Internal (team)' : item.boardVisibility === 2 ? 'Ops directors' : 'Executive'}</BodyText>,
        },
        {
            id: 'queueHealth',
            header: 'Queue Health',
            cell: (item) => (
                <QueueHealthChip $tone={item.queueHealth === 1 ? 'ok' : 'warning'}>
                    {item.queueHealth === 1 ? 'On track' : 'Behind'}
                </QueueHealthChip>
            ),
        },
        {
            id: 'owner',
            header: 'Owner',
            align: 'center',
            cell: (item) => (
                <BodyText>{item.owner?.firstName} {item.owner?.lastName}</BodyText>
            ),
        },
        {
            id: 'lastUpdate',
            header: 'Last update',
            cell: (item) => <OtherText>{formatDateForDisplay(item.updatedAt)}</OtherText>,
        },
    ]

    useQueryPagination({ fetchFunctions: [getOperationPlans], cleanUpFunctions: [clearOperationPlans], params })

    const filters: IFilter[] = [
        {
            type: FilterEnum.SELECT,
            name: 'Status',
            options: planStatuses,
            propName: 'status',
            single: true,
        },
        {
            type: FilterEnum.ASYNC_SELECT,
            name: 'Site',
            link: LinkEnum.OPERATIONAL_SITES,
            propName: 'siteId',
            single: true,
        },
        {
            type: FilterEnum.ASYNC_SELECT,
            name: 'Department',
            link: LinkEnum.DEPARTMENTS,
            propName: 'departmentId',
            single: true,
        },
    ]

    const handlePlanAction = (action: typeof cancelOperationPlan) => async (item: IGetAllOperationPlansSingleResponse) => {
        await dispatch(action({ id: item.id }))
        dispatch(getOperationPlans(params))
    }

    const getOptions = (item: IGetAllOperationPlansSingleResponse): IHonestTableMenuOption<IGetAllOperationPlansSingleResponse>[] => {
        const planMenuOptions: IHonestTableMenuOption<IGetAllOperationPlansSingleResponse>[] = [
            { label: 'Duplicate', onClick: (item) => navigate(`${item.id}/duplicate`) },
        ]

        const cancelOption: IHonestTableMenuOption<IGetAllOperationPlansSingleResponse> = {
            label: 'Cancel',
            color: '#E74C3C',
            onClick: handlePlanAction(cancelOperationPlan),
        }

        if (item.status === 1) {
            planMenuOptions.push(cancelOption)
            planMenuOptions.push({ label: 'Edit', onClick: (item) => navigate(`${item.id}/edit`) })
        } else if (item.status === 2) {
            planMenuOptions.push(
                { label: 'Pause', onClick: handlePlanAction(pauseOperationPlan) },
                { label: 'Complete', onClick: handlePlanAction(completeOperationPlan) },
                cancelOption,
            )
        } else if (item.status === 3) {
            planMenuOptions.push(
                { label: 'Resume', onClick: handlePlanAction(resumeOperationPlan) },
                cancelOption,
            )
        }

        return planMenuOptions
    }

    return (
        <>
            <NavBar
                enableTemporaryManualClosing
                filterOptions={filters}
                params={params}
                input
                defaultValue={params.q}
                placeholder='Search Operation Groups...'
                setParams={setParams}
                searchFields={['name']}
                limit={30}
                visibleFilterOverflow
            />
            <PageWrapper>
                <Flex between center mb='16'>
                    <MainHeadline>Plans</MainHeadline>
                    <Button orange onClick={() => navigate('add')} >Add</Button>
                </Flex>
                {data?.length ? <HonestTable
                    columns={columns}
                    onClick={(item) => navigate(`${item.id}`)}
                    data={data}
                    keyField='id'
                    columnToggle
                    menuOptions={(item) => getOptions(item)}
                /> : <NoData text='No plans to display' />}
            </PageWrapper>
            <PageFooter>
                <Pagination
                    setPage={(page) => handlePagination(page, setParams, 30)}
                    pageCount={Math.ceil(totalPages)}
                    forcePage={(params.page ? params.page : 1) - 1}
                />
            </PageFooter>
        </>
    )
}